import React, { memo } from "react";
import PaymentMessage from "./PaymentMessage.jsx";
import { AudioPreview } from "./AudioPreview.jsx";
import { Document } from "./Document.jsx";
import { ImagePreview } from "./ImagePreview.jsx";
import Interactive from "./Interactive.jsx";
import { VideoPreview } from "./VideoPreview.jsx";

/**
 * Safely parse JSON payload or return the object directly, unwrapping multiple JSON layers if needed
 */
function parsePayload(raw) {
  if (!raw) return null;
  let current = raw;
  let attempts = 0;
  while (typeof current === "string" && attempts < 4) {
    const trimmed = current.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        current = JSON.parse(trimmed);
        attempts++;
      } catch (_) {
        break;
      }
    } else {
      break;
    }
  }
  return (current && typeof current === "object") ? current : null;
}

/**
 * Extract rich template data (header, body, footer, buttons) from message fields
 */
function extractTemplateData(chat) {
  let bodyText = chat.body || chat.caption || null;
  let headerText = null;
  let headerMedia = null;
  let footerText = null;
  let buttons = [];
  let templateName = null;

  const payload = parsePayload(chat.message_paylode) || parsePayload(chat.template_paylode);

  if (Array.isArray(payload)) {
    for (const comp of payload) {
      const type = (comp?.type || "").toUpperCase();
      if (type === "BODY") {
        if (!bodyText && comp.text) bodyText = comp.text;
      } else if (type === "HEADER") {
        if (comp.text) headerText = comp.text;
        const imgUrl = comp.image?.link || comp.image?.url || comp.url || comp.link || (typeof comp.image === "string" ? comp.image : null);
        const vidUrl = comp.video?.link || comp.video?.url || (typeof comp.video === "string" ? comp.video : null);
        const docUrl = comp.document?.link || comp.document?.url || (typeof comp.document === "string" ? comp.document : null);

        if (imgUrl) {
          headerMedia = { type: "image", url: imgUrl };
        } else if (vidUrl) {
          headerMedia = { type: "video", url: vidUrl };
        } else if (docUrl) {
          headerMedia = { type: "document", url: docUrl, filename: comp.document?.filename || "document" };
        } else if (comp.format === "IMAGE") {
          headerMedia = { type: "image", url: null, format: "IMAGE" };
        } else if (comp.format === "VIDEO") {
          headerMedia = { type: "video", url: null, format: "VIDEO" };
        } else if (comp.format === "DOCUMENT") {
          headerMedia = { type: "document", url: null, format: "DOCUMENT" };
        }
      } else if (type === "FOOTER") {
        if (comp.text) footerText = comp.text;
      } else if (type === "BUTTONS" && Array.isArray(comp.buttons)) {
        buttons = comp.buttons;
      }
    }
  } else if (payload && typeof payload === "object") {
    // Check Meta API structure
    if (payload.template) {
      templateName = payload.template.name;
      if (Array.isArray(payload.template.components)) {
        for (const comp of payload.template.components) {
          const type = (comp?.type || "").toLowerCase();
          if (type === "header" && Array.isArray(comp.parameters)) {
            const p = comp.parameters[0];
            const pImg = p?.image?.link || p?.image?.url;
            const pVid = p?.video?.link || p?.video?.url;
            const pDoc = p?.document?.link || p?.document?.url;
            if (pImg) headerMedia = { type: "image", url: pImg };
            else if (pVid) headerMedia = { type: "video", url: pVid };
            else if (pDoc) headerMedia = { type: "document", url: pDoc, filename: p?.document?.filename || "document" };
            else if (p?.text) headerText = p.text;
          }
          if (type === "body" && Array.isArray(comp.parameters)) {
            const params = comp.parameters.map(p => p.text || p.image?.link || "").filter(Boolean);
            if (!bodyText && params.length > 0) {
              bodyText = params.join(" ");
            }
          } else if (type === "button" && comp.sub_type) {
            buttons.push({ text: comp.parameters?.[0]?.text || comp.sub_type, url: comp.parameters?.[0]?.url });
          }
        }
      }
    }
    // Check webhook structure
    const msg = payload.entry?.[0]?.changes?.[0]?.value?.messages?.[0];
    if (msg) {
      if (msg.text?.body) bodyText = msg.text.body;
      if (msg.interactive) {
        bodyText = msg.interactive.body?.text || msg.interactive.button_reply?.title || msg.interactive.list_reply?.title;
      }
      if (msg.button?.text) bodyText = msg.button.text;
    }
  }

  // Fallback if still empty
  if (!bodyText && !headerText && !headerMedia) {
    if (templateName) {
      bodyText = `Template: ${templateName}`;
    } else if (typeof chat.message_paylode === "string" && chat.message_paylode.trim().length > 0) {
      try {
        const parsed = JSON.parse(chat.message_paylode);
        if (parsed.text) bodyText = parsed.text;
        else if (parsed.name) bodyText = `Template: ${parsed.name}`;
      } catch (_) {
        bodyText = chat.message_paylode;
      }
    }
  }

  return {
    headerText,
    headerMedia,
    bodyText: bodyText || (chat.body_type === "template" ? "Template Message" : ""),
    footerText,
    buttons,
    templateName
  };
}

/**
 * Image Header Renderer with error fallback
 */
const TemplateHeaderImage = ({ url }) => {
  const [hasError, setHasError] = React.useState(false);

  if (!url) {
    return (
      <div className="mb-2 p-2 bg-light border rounded text-muted" style={{ fontSize: "11px" }}>
        <span>🖼️ Header Image (No URL provided in payload)</span>
      </div>
    );
  }

  return (
    <div className="mb-2">
      <div className="rounded overflow-hidden border bg-light">
        {!hasError ? (
          <img
            src={url}
            alt="Template Header"
            onError={() => setHasError(true)}
            style={{ width: "100%", maxHeight: "200px", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div className="p-3 text-center text-muted" style={{ fontSize: "12px" }}>
            <div style={{ fontSize: "1.4rem" }}>⚠️</div>
            <div>Image could not be loaded</div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Dedicated TemplateMessage component rendering Header, Body, Footer, and Action Buttons
 */
const TemplateMessage = memo(({ chat }) => {
  const { headerText, headerMedia, bodyText, footerText, buttons } = extractTemplateData(chat);

  return (
    <div className="template-message" style={{ minWidth: "160px", maxWidth: "100%" }}>
      {/* Header Media */}
      {headerMedia?.type === "image" && (
        <TemplateHeaderImage url={headerMedia.url} />
      )}
      {headerMedia?.type === "video" && (
        <div className="mb-2">
          <div className="rounded overflow-hidden border bg-light">
            {headerMedia.url ? (
              <video src={headerMedia.url} controls style={{ width: "100%", maxHeight: "200px", display: "block" }} />
            ) : (
              <div className="p-2 text-muted" style={{ fontSize: "11px" }}>🎥 Video (No URL in payload)</div>
            )}
          </div>
        </div>
      )}
      {headerMedia?.type === "document" && (
        <div className="mb-2">
          <div className="p-2 bg-white rounded d-flex align-items-center gap-2 border">
            <span style={{ fontSize: "1.4rem" }}>📄</span>
            <a href={headerMedia.url || "#"} target="_blank" rel="noopener noreferrer" className="text-truncate text-decoration-none fw-semibold" style={{ fontSize: "0.85rem", color: "#00a884" }}>
              {headerMedia.filename || "Document"}
            </a>
          </div>
        </div>
      )}

      {/* Header Text */}
      {headerText && (
        <h6 className="fw-bold mb-1" style={{ fontSize: "0.95rem", color: "#111B21" }}>
          {headerText}
        </h6>
      )}

      {/* Body Text */}
      {bodyText && (
        <p className="m-0 mb-1" style={{ fontSize: "0.92rem", color: "#111B21", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {bodyText}
        </p>
      )}

      {/* Footer Text */}
      {footerText && (
        <p className="m-0 mt-1" style={{ fontSize: "0.75rem", color: "#667781", fontStyle: "italic" }}>
          {footerText}
        </p>
      )}

      {/* WhatsApp Action Buttons */}
      {buttons && buttons.length > 0 && (
        <div className="d-flex flex-column gap-1 mt-2 pt-2 border-top">
          {buttons.map((btn, idx) => {
            const btnText = btn.text || btn.title || btn.display_text || `Button ${idx + 1}`;
            const isUrl = btn.type === "URL" || btn.url;
            const isPhone = btn.type === "PHONE_NUMBER" || btn.phone_number;
            const href = isUrl ? (btn.url || "#") : isPhone ? `tel:${btn.phone_number}` : undefined;

            return href ? (
              <a
                key={idx}
                href={href}
                target={isUrl ? "_blank" : undefined}
                rel={isUrl ? "noopener noreferrer" : undefined}
                className="btn btn-sm w-100 d-flex align-items-center justify-content-center gap-1 text-decoration-none shadow-sm"
                style={{
                  backgroundColor: "#ffffff",
                  color: "#00a884",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  fontSize: "0.82rem",
                  fontWeight: "500",
                  padding: "6px 10px"
                }}
              >
                <span>{isUrl ? "🔗" : "📞"}</span>
                <span>{btnText}</span>
              </a>
            ) : (
              <div
                key={idx}
                className="btn btn-sm w-100 d-flex align-items-center justify-content-center gap-1 shadow-sm"
                style={{
                  backgroundColor: "#ffffff",
                  color: "#00a884",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  fontSize: "0.82rem",
                  fontWeight: "500",
                  padding: "6px 10px",
                  cursor: "default"
                }}
              >
                <span>💬</span>
                <span>{btnText}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

/**
 * Debug Payload View component when message has no standard displayable text
 */
const DebugPayloadCard = memo(({ chat, bodyType }) => {
  const [expanded, setExpanded] = React.useState(true);
  const typeName = bodyType || chat.body_type || chat.type || "unknown";

  let rawPayload = chat.message_paylode || chat.template_paylode || chat.payload || null;
  if (typeof rawPayload === "string") {
    try {
      rawPayload = JSON.parse(rawPayload);
    } catch (_) {
      // keep as string
    }
  }

  const payloadStr = rawPayload
    ? (typeof rawPayload === "object" ? JSON.stringify(rawPayload, null, 2) : String(rawPayload))
    : null;

  return (
    <div className="py-1" style={{ minWidth: "180px", maxWidth: "100%" }}>
      <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
        <span
          className="badge bg-secondary-subtle text-dark border px-2 py-1"
          style={{ fontSize: "0.75rem", fontFamily: "monospace" }}
        >
          type: {typeName}
        </span>
        {payloadStr && (
          <button
            type="button"
            className="btn btn-link p-0 text-muted"
            style={{ fontSize: "0.7rem", textDecoration: "none" }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Hide payload ▲" : "Show payload ▼"}
          </button>
        )}
      </div>

      {chat.body && typeof chat.body === "string" && (
        <div className="mb-1 text-dark" style={{ fontSize: "0.9rem", wordBreak: "break-word" }}>
          {chat.body}
        </div>
      )}

      {payloadStr && expanded && (
        <div className="mt-1">
          <div className="text-muted mb-1" style={{ fontSize: "0.68rem", fontWeight: "600", letterSpacing: "0.3px" }}>
            PAYLOAD:
          </div>
          <pre
            className="p-2 bg-light border rounded text-dark mb-0"
            style={{
              fontSize: "0.72rem",
              fontFamily: "Consolas, Monaco, monospace",
              maxHeight: "180px",
              overflowX: "auto",
              overflowY: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
              lineHeight: "1.3"
            }}
          >
            {payloadStr}
          </pre>
        </div>
      )}

      {!payloadStr && !chat.body && (
        <div className="text-muted fst-italic" style={{ fontSize: "0.78rem" }}>
          (No payload content available)
        </div>
      )}
    </div>
  );
});

/**
 * Extract message text from all possible message payload locations
 */
function extractMessageText(chat) {
  if (chat.body && typeof chat.body === "string" && chat.body.trim().length > 0) {
    return chat.body;
  }
  if (chat.caption && typeof chat.caption === "string" && chat.caption.trim().length > 0) {
    return chat.caption;
  }
  if (chat.text && typeof chat.text === "string" && chat.text.trim().length > 0) {
    return chat.text;
  }

  const payload = parsePayload(chat.message_paylode) || parsePayload(chat.template_paylode) || parsePayload(chat.payload);
  if (payload) {
    // 1. Meta Webhook payload structure
    const msg = payload.entry?.[0]?.changes?.[0]?.value?.messages?.[0] || payload.messages?.[0];
    if (msg) {
      if (msg.text?.body) return msg.text.body;
      if (msg.interactive?.button_reply?.title) return msg.interactive.button_reply.title;
      if (msg.interactive?.button_reply?.id) return msg.interactive.button_reply.id;
      if (msg.interactive?.list_reply?.title) return msg.interactive.list_reply.title;
      if (msg.interactive?.list_reply?.description) return msg.interactive.list_reply.description;
      if (msg.interactive?.nfm_reply?.response_json) return msg.interactive.nfm_reply.response_json;
      if (msg.interactive?.body?.text) return msg.interactive.body.text;
      if (msg.button?.text) return msg.button.text;
      if (msg.button?.payload) return msg.button.payload;
      if (msg.reaction?.emoji) return msg.reaction.emoji;
      if (msg.image?.caption) return msg.image.caption;
      if (msg.video?.caption) return msg.video.caption;
      if (msg.document?.caption || msg.document?.filename) return msg.document.caption || msg.document.filename;
      if (msg.system?.body) return msg.system.body;
      if (msg.type === "text" && msg.text?.body) return msg.text.body;
      if (msg.type && typeof msg[msg.type] === "object") {
        const sub = msg[msg.type];
        if (sub?.title || sub?.text || sub?.name || sub?.caption) {
          return sub.title || sub.text || sub.name || sub.caption;
        }
      }
    }

    // 2. Direct JSON properties
    if (typeof payload.text === "string") return payload.text;
    if (payload.text?.body) return payload.text.body;
    if (typeof payload.body === "string") return payload.body;
    if (typeof payload.message === "string") return payload.message;
    if (payload.template?.name) return `Template: ${payload.template.name}`;
    if (payload.name) return `Template: ${payload.name}`;
  }

  // 3. Fallback from extractTemplateData
  const templateBody = extractTemplateData(chat).bodyText;
  if (templateBody && templateBody.trim().length > 0 && templateBody !== "Template Message") {
    return templateBody;
  }

  // 4. Raw string in message_paylode if not JSON
  if (typeof chat.message_paylode === "string" && chat.message_paylode.trim().length > 0 && !chat.message_paylode.startsWith("{") && !chat.message_paylode.startsWith("[")) {
    return chat.message_paylode;
  }

  return null;
}

/**
 * MessageContent — renders the appropriate media or text component
 * based on the message body_type.
 */
const MessageContent = memo(({ chat, sending_type }) => {
  const bodyType = chat.body_type;

  switch (bodyType) {
    case "template":
      return <TemplateMessage chat={chat} />;

    case "text":
    case "button": {
      const textToDisplay = extractMessageText(chat);
      if (textToDisplay) {
        return (
          <p className="m-0 mb-1" style={{ fontSize: "0.95rem", color: "#111B21", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {textToDisplay}
          </p>
        );
      }
      return <DebugPayloadCard chat={chat} bodyType={bodyType} />;
    }

    case "image":
      return <ImagePreview chat={chat} sending_type={sending_type} />;

    case "video":
      return <VideoPreview chat={chat} sending_type={sending_type} />;

    case "audio":
    case "voice":
      return <AudioPreview chat={chat} sending_type={sending_type} />;

    case "document":
      return <Document chat={chat} sending_type={sending_type} />;

    case "reaction":
      return <p className="m-0" style={{ fontSize: "1.5rem" }}>{chat.emoji || chat.body || "👍"}</p>;

    case "sticker":
      return <ImagePreview chat={chat} sending_type={sending_type} />;

    case "location": {
      let msg = null;
      try {
        const payload = parsePayload(chat.message_paylode);
        msg = payload?.entry?.[0]?.changes?.[0]?.value?.messages?.[0];
      } catch (_) { /* ignore */ }

      if (msg?.location) {
        const { latitude, longitude } = msg.location;
        return (
          <div
            className="border rounded bg-white overflow-hidden mb-1 shadow-sm"
            style={{ width: "220px", cursor: "pointer" }}
            onClick={() => window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, "_blank")}
            role="link"
            tabIndex={0}
            aria-label="View on Google Maps"
            onKeyDown={e => e.key === "Enter" && window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, "_blank")}
          >
            <div className="bg-light p-3 text-center text-danger" style={{ fontSize: "2rem" }}>📍</div>
            <div className="p-2 border-top">
              <div className="fw-bold" style={{ fontSize: "0.85rem" }}>Shared Location</div>
              <small className="text-primary">View on Map →</small>
            </div>
          </div>
        );
      }
      return <p className="m-0 mb-1" style={{ fontSize: "0.95rem" }}>📍 {chat.body || chat.location || "Shared Location"}</p>;
    }

    case "contacts": {
      let msg = null;
      try {
        const payload = parsePayload(chat.message_paylode);
        msg = payload?.entry?.[0]?.changes?.[0]?.value?.messages?.[0];
      } catch (_) { /* ignore */ }

      if (msg?.contacts) {
        return (
          <>
            {msg.contacts.map((contact, i) => (
              <div key={i} className="p-2 border rounded bg-white shadow-sm mb-1" style={{ minWidth: "220px" }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span style={{ fontSize: "2rem" }}>👤</span>
                  <div className="overflow-hidden">
                    <div className="fw-bold text-truncate" style={{ fontSize: "0.9rem" }}>
                      {contact.name?.formatted_name || contact.name?.first_name || "Unknown"}
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      {contact.phones?.[0]?.phone || "No phone"}
                    </div>
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-outline-primary w-100 py-1"
                  style={{ fontSize: "0.75rem" }}
                  onClick={() => window.open(`tel:${contact.phones?.[0]?.phone}`)}
                >
                  View Contact
                </button>
              </div>
            ))}
          </>
        );
      }
      return <p className="m-0 mb-1" style={{ fontSize: "0.95rem" }}>👤 {chat.body || chat.contact || "Contact Card"}</p>;
    }

    case "interactive_payment":
      return <PaymentMessage paymentData={chat} message={chat.body} />;

    case "interactive":
      return <Interactive paymentData={chat} />;

    default: {
      const fallbackText = extractMessageText(chat);
      if (fallbackText) {
        return (
          <p className="m-0 mb-1" style={{ fontSize: "0.95rem", color: "#111B21", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {fallbackText}
          </p>
        );
      }
      return <DebugPayloadCard chat={chat} bodyType={bodyType} />;
    }
  }
});

export default MessageContent;